import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import api from "@/lib/api";

const CTRL_STATUS = {
  ready: { label: "Ready", color: "#10B981", icon: CheckCircle2 },
  in_progress: { label: "In Progress", color: "#F59E0B", icon: Clock },
  gap: { label: "Gap", color: "#FF3366", icon: AlertCircle },
};

export default function Compliance() {
  const [frameworks, setFrameworks] = useState(null);

  useEffect(() => {
    api.get("/compliance").then(({ data }) => setFrameworks(data));
  }, []);

  if (!frameworks) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="animate-spin text-cyber" size={30} /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ink">Compliance Readiness</h1>
        <p className="text-sm text-muted mt-1">Continuous control tracking across your target frameworks.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {frameworks.map((fw) => (
          <div key={fw.key} className="card-surface rounded-2xl p-5" data-testid={`framework-${fw.key}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-display font-bold text-ink">{fw.name}</span>
              <span className="font-display font-extrabold text-2xl" style={{ color: fw.color }}>{fw.readiness}%</span>
            </div>
            <Progress value={fw.readiness} className="h-2 bg-[var(--surface-2)]"
              style={{ "--tw-progress-color": fw.color }} />
            <p className="text-xs text-muted mt-2">{fw.ready_controls}/{fw.total_controls} controls ready</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {frameworks.map((fw, idx) => (
          <motion.div key={fw.key}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.06 }}
            className="card-surface rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: fw.color }} />
              <h3 className="font-display font-semibold text-ink">{fw.name}</h3>
            </div>
            <p className="text-sm text-muted mb-4">{fw.description}</p>
            <div className="space-y-2">
              {fw.controls.map((c) => {
                const s = CTRL_STATUS[c.status];
                const Icon = s.icon;
                return (
                  <div key={c.id} className="flex items-center justify-between rounded-lg border border-line px-3 py-2.5">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className="text-xs font-mono text-muted shrink-0">{c.id}</span>
                      <span className="text-sm text-ink truncate">{c.name}</span>
                    </div>
                    <span className="flex items-center gap-1.5 text-xs font-medium shrink-0" style={{ color: s.color }}>
                      <Icon size={14} /> {s.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
